import objectFitImages from 'object-fit-images';
import 'lazysizes';

class WPImg extends HTMLElement {
  /**
   * Tag name of element
   * @readonly
   * @static
   */
  static get is() {
    return 'wp-img';
  }

  /**
   * Getter for `alt` attribute
   */
  get alt() {
    return this.hasAttribute('alt') ? this.getAttribute('alt') : '';
  }

  /**
   * Setter for `alt` attribute
   */
  set alt(value) {
    if (value) {
      this.setAttribute('alt', value);
    } else {
      this.removeAttribute('alt');
    }
  }

  /**
   * Getter for `animation` attribute
   */
  get animation() {
    return this.hasAttribute('animation')
      ? this.getAttribute('animation')
      : 'fadeIn';
  }

  /**
   * Setter for `animation` attribute
   */
  set animation(value) {
    if (value) {
      this.setAttribute('animation', value);
    } else {
      this.removeAttribute('animation');
    }
  }

  /**
   * Getter for `src` attribute
   */
  get src() {
    return this.hasAttribute('src') ? this.getAttribute('src') : '';
  }

  /**
   * Setter for `src` attribute
   */
  set src(value) {
    if (value) {
      this.setAttribute('src', value);
    } else {
      this.removeAttribute('src');
    }
  }

  /**
   * Getter for `srcset` attribute
   */
  get srcset() {
    return this.hasAttribute('srcset') ? this.getAttribute('srcset') : '';
  }

  /**
   * Setter for `srcset` attribute
   */
  set srcset(value) {
    if (value) {
      this.setAttribute('srcset', value);
    } else {
      this.removeAttribute('srcset');
    }
  }

  /**
   * Getter for `loaded` attribute
   */
  get loaded() {
    return this.hasAttribute('loaded');
  }

  /**
   * Setter for `loaded` attribute
   */
  set loaded(value) {
    if (Boolean(value)) {
      this.setAttribute('loaded', '');
    } else {
      this.removeAttribute('loaded');
    }
  }

  /**
   * Getter for `fit` attribute
   */
  get fit() {
    return this.getAttribute('fit');
  }

  /**
   * Setter for `fit` attribute
   */
  set fit(value) {
    if (value) {
      this.setAttribute('fit', value);
    } else {
      this.removeAttribute('fit');
    }
  }

  /**
   * Getter for `position` attribute
   */
  get position() {
    return this.hasAttribute('position')
      ? this.getAttribute('position')
      : '50% 50%';
  }

  /**
   * Setter for `position` attribute
   */
  set position(value) {
    if (value) {
      this.setAttribute('position', value);
    } else {
      this.removeAttribute('position');
    }
  }

  /**
   * Lifecycle event when element is attached to DOM
   */
  connectedCallback() {
    this._placeholder = this.querySelector('[data-placeholder]');
    this._img = this.querySelector('[data-img]');

    // If no placeholder, set opacity to zero (don't rely on classes alone)
    if (!this._placeholder) {
      this.style.opacity = '0';
    }

    // If loaded, return
    if (this.loaded) {
      return;
    }

    // If no src attribute defined, return
    if (!this.src && !this.srcset) {
      return;
    }

    // If no img defined, attach
    if (!this._img) {
      this.attach();
    }

    // Handle lazyload
    this._img.addEventListener('lazyloaded', evt => {
      if (this._placeholder) {
        this._placeholder.style.transition = 'opacity 1s ease';
        this._placeholder.style.opacity = '0';

        this._placeholder.$once('transitionend', () => {
          this.$emit('animationend');
        });
      } else {
        if (this.animation) {
          this.classList.add(this.animation);
        }
      }

      this.loaded = true;
    });
  }

  _applyObjectFit(img) {
    img.style.objectFit = this.fit;
    img.style.objectPosition = this.position;
    img.style.fontFamily = `
      object-fit: ${this.fit}; 
      object-position: ${this.position}`;
    img.style.height = '100%';
    img.style.width = '100%';
    img.style.display = 'block';

    objectFitImages(img);
  }

  attach() {
    if (this._img) {
      return;
    }

    const img = document.createElement('img');

    // img.srcset =
    //   'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
    // img.src = this.src;
    img.alt = this.alt;
    img.className = 'lazyload';
    img.setAttribute('data-sizes', 'auto');
    img.setAttribute('data-srcset', this.srcset);
    img.setAttribute('data-src', this.src);
    img.setAttribute('data-img', '');

    // Handle fit
    if (this.fit) {
      this._applyObjectFit(img);
    }

    this.insertBefore(img, this.firstChild);
    this._img = img;
  }
}

customElements.define(WPImg.is, WPImg);
